// Supabase IntegrationMethods: thin wrappers over the batch-6 RPCs (Tasks 14–16). P0001 messages are stable
// snake_case codes and become IntegrationApiError codes; anything else (network, PostgREST) becomes "unknown".
import type { SupabaseClient } from "@supabase/supabase-js";
import type { DataProvider } from "ra-core";
import {
  checkWebhookUrlSyntax,
  type ApiScope,
  type WebhookEvent,
} from "@/components/gavion/domain/integrations";
import {
  IntegrationApiError,
  type GranolaConnectionStatus,
  type IntegrationMethods,
} from "./types";

const SAFE_CODE = /^[a-z_]{2,40}$/;
const GRANOLA_STATUSES = new Set<GranolaConnectionStatus["status"]>([
  "none",
  "connected",
  "invalid",
  "trial_ended",
  "error",
]);

/** gavion-granola-connect body → status; anything unexpected reads as « none ». */
export function granolaStatusFrom(v: unknown): GranolaConnectionStatus {
  const o = (v ?? {}) as Record<string, unknown>;
  const s = o.status as GranolaConnectionStatus["status"];
  const str = (x: unknown) => (typeof x === "string" ? x : null);
  return {
    status: GRANOLA_STATUSES.has(s) ? s : "none",
    connectedAt: str(o.connectedAt),
    lastSyncAt: str(o.lastSyncAt),
    lastSuccessAt: str(o.lastSuccessAt),
    lastError: str(o.lastError),
  };
}

/** The base tables deny `select *` (secret columns); the browser reads these safe views instead. */
export const GAVION_RESOURCE_VIEWS: Readonly<Record<string, string>> = {
  api_keys: "api_keys_admin",
  webhook_endpoints: "webhook_endpoints_admin",
};

export function withResourceViews<T extends DataProvider>(dp: T): T {
  const view = (r: string) => GAVION_RESOURCE_VIEWS[r] ?? r;
  return {
    ...dp,
    getList: (r: string, p: never) => dp.getList(view(r), p),
    getOne: (r: string, p: never) => dp.getOne(view(r), p),
    getMany: (r: string, p: never) => dp.getMany(view(r), p),
    getManyReference: (r: string, p: never) => dp.getManyReference(view(r), p),
  } as T;
}

export function createSupabaseIntegrationMethods(
  getClient: () => SupabaseClient,
): IntegrationMethods {
  const rpc = async <T>(
    fn: string,
    args: Record<string, unknown>,
  ): Promise<T> => {
    const { data, error } = await getClient().rpc(fn, args);
    if (error)
      throw new IntegrationApiError(
        /^[a-z_]{2,40}$/.test(error.message) ? error.message : "unknown",
      );
    return data as T;
  };
  /** POST gavion-granola-connect/<route>; a non-2xx becomes IntegrationApiError(code), never a raw status. */
  const granola = async (route: string, body: Record<string, unknown> = {}) => {
    const { data, error } = await getClient().functions.invoke(
      `gavion-granola-connect/${route}`,
      { method: "POST", body },
    );
    if (!error) return granolaStatusFrom(data);
    const res = (error as { context?: unknown }).context;
    let code = "network";
    if (res instanceof Response) {
      code = res.status === 401 ? "unauthorized" : "unknown";
      try {
        const b = (await res.json()) as { error?: unknown };
        if (typeof b?.error === "string" && SAFE_CODE.test(b.error))
          code = b.error;
      } catch {
        // keep the status-based code
      }
    }
    throw new IntegrationApiError(code);
  };
  return {
    granolaStatus: () => granola("status"),
    granolaConnect: (apiKey) => granola("store", { api_key: apiKey }),
    granolaSyncNow: () => granola("sync"),
    async granolaDisconnect() {
      await granola("disconnect");
    },
    async granolaAddManualNote(i) {
      const r = await rpc<{
        id: number;
        created: boolean;
        match_status: "matched" | "unmatched";
      }>("add_manual_meeting_note", {
        p_note: {
          summary_markdown: i.summaryMarkdown,
          title: i.title,
          started_at: i.startedAt,
          company_id: i.companyId,
          deal_id: i.dealId,
          contact_id: i.contactId,
          share_url: i.shareUrl,
          attendees: i.attendees,
        },
      });
      return { id: r.id, created: r.created, matchStatus: r.match_status };
    },
    async granolaAssociateNote(noteId, t) {
      await rpc("associate_meeting_note", {
        p_note_id: noteId,
        p_company_id: t.companyId,
        p_deal_id: t.dealId,
        p_contact_id: t.contactId,
      });
    },
    async granolaIgnoreNote(noteId) {
      await rpc("ignore_meeting_note", { p_note_id: noteId });
    },
    async granolaUnignoreNote(noteId) {
      const r = await rpc<{ match_status: "matched" | "unmatched" } | null>(
        "unignore_meeting_note",
        { p_note_id: noteId },
      );
      return {
        matchStatus: r?.match_status === "matched" ? "matched" : "unmatched",
      };
    },
    async granolaNoteSuggestion(noteId) {
      const r = await rpc<Record<string, unknown> | null>(
        "meeting_note_suggestion",
        { p_note_id: noteId },
      );
      const reasons = ["booked_demo", "phone", "attendee", "title"] as const;
      if (!r || !reasons.includes(r.reason as (typeof reasons)[number]))
        return null;
      const id = (v: unknown) => (v == null ? null : Number(v));
      return {
        companyId: Number(r.company_id),
        companyName: String(r.company_name ?? ""),
        dealId: id(r.deal_id),
        contactId: id(r.contact_id),
        reason: r.reason as (typeof reasons)[number],
        demoAt: typeof r.demo_at === "string" ? r.demo_at : null,
      };
    },
    async granolaCreateTasks(noteId, items, dueAt) {
      const r = await rpc<{ created: number; skipped: number }>(
        "create_meeting_note_tasks",
        { p_note_id: noteId, p_items: items, p_due_at: dueAt },
      );
      return { created: r.created, skipped: r.skipped };
    },
    async apiKeyCreate(name, scopes) {
      const r = await rpc<{
        uid: string;
        name: string;
        key_prefix: string;
        scopes: ApiScope[];
        plain_key: string;
      }>("create_api_key", { p_name: name, p_scopes: scopes });
      return {
        uid: r.uid,
        name: r.name,
        keyPrefix: r.key_prefix,
        scopes: r.scopes,
        plainKey: r.plain_key,
      };
    },
    async apiKeyRevoke(uid) {
      await rpc("revoke_api_key", { p_uid: uid });
    },
    async webhookEndpointCreate(url, events) {
      const check = checkWebhookUrlSyntax(url);
      if (!check.ok) throw new IntegrationApiError("invalid_url");
      const r = await rpc<{
        uid: string;
        url: string;
        events: WebhookEvent[];
        secret: string;
      }>("create_webhook_endpoint", {
        p_url: check.url.href,
        p_events: events,
      });
      return { uid: r.uid, url: r.url, events: r.events, secret: r.secret };
    },
    async webhookEndpointSetEnabled(uid, enabled) {
      await rpc("set_webhook_endpoint_enabled", {
        p_uid: uid,
        p_enabled: enabled,
      });
    },
    async webhookEndpointDelete(uid) {
      await rpc("delete_webhook_endpoint", { p_uid: uid });
    },
    async inboundStatus() {
      const r = await rpc<{
        enabled?: boolean;
        secret_set_at?: string | null;
        last_received_at?: string | null;
      } | null>("inbound_webhook_status", {});
      return {
        enabled: r?.enabled === true,
        secretSetAt: r?.secret_set_at ?? null,
        lastReceivedAt: r?.last_received_at ?? null,
      };
    },
    async inboundRotateSecret() {
      const r = await rpc<{ secret: string }>(
        "rotate_inbound_webhook_secret",
        {},
      );
      return { secret: r.secret };
    },
    async inboundSetEnabled(enabled) {
      await rpc("set_inbound_webhook_enabled", { p_enabled: enabled });
    },
  };
}
